/**
 * To turn a text input into a timepicker input, do the following:
 * 
 *  - Make sure the input is wrapped in a div which has position != static
 *  - Add the attribute data-timepicker to the input
 * 
 * Alternatively, you can pass a JQuery object for the text input into the function initTimepicker().
 * 
 * Optionally, add a data-timepicker-min-increment attribute to the input to control
 * the minute increment (default 5 mins).
 * 
 */

import { each } from "lodash";

declare const jQuery: any;
const $ = jQuery;

interface Time
{
    hour: number;
    min: number;
}

export function initTimepickers(): void
{
    $(`input[type="text"][data-timepicker]`).each(function () {
        initTimepicker($(this));
    });
}

// $input - JQuery - must be text input wrapped in a div with position != static
function initTimepicker($input)
{
    const $timepicker = $(`<div class="timepicker">`).insertAfter($input);
    const $inner = $(`<div class="timepicker-main">`).appendTo($timepicker);
    const $hour = $(`<div class="timepicker-h">`).appendTo($inner);
    const $hourDisplay = $(`<div class="timepicker-display">`).appendTo($hour);
    const $hourItems = $(`<div class="timepicker-items">`).appendTo($hour);
    $(`<div class="timepicker-sep"> : </div>`).appendTo($inner);
    const $min = $(`<div class="timepicker-m">`).appendTo($inner);
    const $minDisplay = $(`<div class="timepicker-display">`).appendTo($min);
    const $minItems = $(`<div class="timepicker-items">`).appendTo($min);
    const $okBtn = $(`<div class="timepicker-ok">Select</div>`);
    $(`<div class="timepicker-btn-wrap">`).append($okBtn).appendTo($timepicker);
    const minIncrement: number = parseInt($input.attr("data-timepicker-min-increment", 10)) || 5;

    const zeroPad = (n: number): string => {
        return n < 10 ? "0" + n : n.toString();
    };

    each([[$hourItems, 24, 1], [$minItems, 60, minIncrement]], (divNum: any[]) => {
        const $e = divNum[0];
        const n: number = divNum[1];
        const inc: number = divNum[2];
        for (let i = 0; i < n; i += inc)
        {
            const num: string = zeroPad(i);
            $e.append($(`<div class="timepicker-opt" data-value="${i}">${num}</div>`));
        }
    });
    
    const getTimeFromInput = (): Time|undefined => {
        const value: string = $.trim($input.val() || "").replace(/\s/g, "");
        const numStrings: string[] = value.split(/\:/g);
        const hour: number = parseInt(numStrings[0], 10);
        const min: number = parseInt(numStrings[1], 10);
        if (numStrings.length !== 2 || isNaN(hour) || isNaN(min) || hour < 0 || hour > 23 || min < 0 || min > 59)
        {
            return undefined;
        }
        return { hour, min };
    };

    const currTimepickerData: Time = { hour: 0, min: 0 };
    
    const updateInputFromTime = (time: Time): void => {
        const hour: string = zeroPad(time.hour);
        const min: string = zeroPad(time.min);
        $input.val(hour + ":" + min);
    };

    const updateInputFromCurrentTimepickerData = () => {
        updateInputFromTime(currTimepickerData);
    };

    const updateDisplaysFromCurrentTimePickerData = () => {
        $hourDisplay.html(zeroPad(currTimepickerData.hour));
        $minDisplay.html(zeroPad(currTimepickerData.min));
    };
    
    const initInputValue = () => {
        const time: Time = getTimeFromInput();
        if (!time)
        {
            if ($input.val() !== "")
            {
                $input.val("").trigger("change");
            }
            return;
        }
        currTimepickerData.hour = time.hour;
        currTimepickerData.min = time.min;
        updateInputFromCurrentTimepickerData();
    };

    const makeSelection = () => {
        updateInputFromCurrentTimepickerData();
        hideTimepicker();
        $input.trigger("change");
    };

    initInputValue();

    const showTimepicker = () => {
        const time: Time = getTimeFromInput() || { hour: 0, min: 0 };
        currTimepickerData.hour = time.hour;
        currTimepickerData.min = time.min;
        updateDisplaysFromCurrentTimePickerData();
        $hourItems.removeClass("show");
        $minItems.removeClass("show");
        $timepicker.addClass("show");
    };

    $input.on("change", function (event) {
        if (!getTimeFromInput())
        {
            if ($input.val() !== "")
            {
                $input.val("").trigger("change");
            }
        }
    }).on("focus", function (event) {
        showTimepicker();
    }).on("click", function (event) {
        event.stopPropagation();
    });

    const hideTimepicker = () => {
        $hourItems.removeClass("show");
        $minItems.removeClass("show");
        $timepicker.removeClass("show");
    };

    $hourDisplay.on("click", function (event) {
        event.stopPropagation();
        $hourItems.toggleClass("show");
        $minItems.removeClass("show");
    });

    $minDisplay.on("click", function (event) {
        event.stopPropagation();
        $minItems.toggleClass("show");
        $hourItems.removeClass("show");
    });

    $hourItems.find(".timepicker-opt").on("click", function (event) {
        event.stopPropagation();
        currTimepickerData.hour = parseInt($(this).attr("data-value"), 10);
        $hourItems.removeClass("show");
        $minItems.removeClass("show");
        updateDisplaysFromCurrentTimePickerData();
    });

    $minItems.find(".timepicker-opt").on("click", function (event) {
        event.stopPropagation();
        currTimepickerData.min = parseInt($(this).attr("data-value"), 10);
        $hourItems.removeClass("show");
        $minItems.removeClass("show");
        updateDisplaysFromCurrentTimePickerData();
    });

    $okBtn.on("click", function (event) {
        event.stopPropagation();
        makeSelection();
    });

    $timepicker.on("click", function (event) {
        event.stopPropagation();
    });

    $(document.body).on("click", function (event) {
        hideTimepicker();
    });

    return $timepicker;
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].initTimepicker = initTimepicker;
